import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { PlayerService } from './player.service';
import { PlayerModel, ListPlayersQuery } from './player.type';
import { PlayerFormDialog } from './player-form-dialog';
import { PlayerBulkUpdateDialog } from './player-bulk-update-dialog';
import { ConfirmDialog } from '../shared/confirm-dialog/confirm-dialog';
import { Header } from '../shared/header/header';
import { distinctUntilChanged, EMPTY, fromEvent, map, startWith, Subscription, switchMap, timer } from 'rxjs';
import { AuthService } from '../auth/auth.service';

@Component({
  selector: 'app-player',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatPaginatorModule,
    Header,
  ],
  templateUrl: './player.html',
  styleUrls: ['./player.scss'],
})
export class Player {
  readonly canEdit = inject(AuthService).canWrite('system');
  displayedColumns: string[] = ['name', 'quality', 'rarity_id', 'overall', 'market_price', 'active', 'actions'];

  players: PlayerModel[] = [];
  loading: boolean = true;
  pageIndex: number = 0;
  pageSize: number = 50;
  total: number = 0;
  totalPages: number = 0;
  lastUpdate: number = 0;

  filterQuality: string = '';
  filterRarityId: string = '';
  filterActive: string = '';

  qualityOptions = [
    { value: 'bronze', label: 'Bronze' },
    { value: 'silver', label: 'Silver' },
    { value: 'gold', label: 'Gold' },
    { value: 'special', label: 'Special' },
  ];

  constructor(
    private playerService: PlayerService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) { }

  private destroyRef = inject(DestroyRef);
  private listSub?: Subscription;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.listSub?.unsubscribe());
    this.advancedLoad();
  }

  advancedLoad(): void {
    const visibility$ = fromEvent(document, 'visibilitychange').pipe(
      startWith(null),
      map(() => !document.hidden),
      distinctUntilChanged()
    );

    visibility$
      .pipe(
        switchMap(visible =>
          visible ? timer(0, 30_000) : EMPTY
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.loadPlayers());

    visibility$
      .pipe(
        switchMap(visible =>
          visible ? timer(0, 1_000) : EMPTY
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.lastUpdate++;
      });
  }

  loadPlayers(): void {
    this.loading = true;

    const query: ListPlayersQuery = {
      offset: this.pageIndex * this.pageSize,
      limit: this.pageSize,
    };

    if (this.filterQuality) {
      query.quality = this.filterQuality;
    }

    if (this.filterRarityId) {
      query.rarity_id = parseInt(this.filterRarityId, 10);
    }

    if (this.filterActive !== '') {
      query.active = this.filterActive === 'true';
    }

    this.listSub?.unsubscribe();
    this.listSub = this.playerService.getPlayers(query).subscribe({
      next: (response) => {
        this.players = response.players ?? [];
        this.total = response.total || 0;
        this.totalPages = Math.ceil(this.total / this.pageSize);
        this.loading = false;
        this.lastUpdate = 0;
      },
      error: (error) => {
        console.error('Error loading players:', error);
        this.snackBar.open('Error loading players', 'Close', { duration: 5000 });
        this.loading = false;
      },
    });
  }

  refreshPlayers(): void {
    this.loadPlayers();
  }

  applyFilters(): void {
    this.pageIndex = 0;
    this.loadPlayers();
  }

  clearFilters(): void {
    this.filterQuality = '';
    this.filterRarityId = '';
    this.filterActive = '';
    this.pageIndex = 0;
    this.loadPlayers();
  }

  onPage(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.loadPlayers();
  }

  openCreateDialog(): void {
    const isMobile = window.innerWidth <= 768;
    const dialogRef = this.dialog.open(PlayerFormDialog, {
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '500px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : '90vh',
      disableClose: true,
      data: { mode: 'create' },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadPlayers();
    });
  }

  openEditDialog(player: PlayerModel): void {
    const isMobile = window.innerWidth <= 768;
    const dialogRef = this.dialog.open(PlayerFormDialog, {
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '500px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : '90vh',
      disableClose: true,
      data: { mode: 'edit', player },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadPlayers();
    });
  }

  openBulkUpdateDialog(): void {
    const isMobile = window.innerWidth <= 768;
    const dialogRef = this.dialog.open(PlayerBulkUpdateDialog, {
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '500px',
      maxWidth: isMobile ? '100vw' : '90vw',
      height: isMobile ? '100vh' : undefined,
      maxHeight: isMobile ? '100vh' : '90vh',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadPlayers();
    });
  }

  toggleActive(player: PlayerModel): void {
    const newActiveStatus = !player.active;

    this.playerService.updatePlayerActive(player.id, newActiveStatus).subscribe({
      next: () => {
        this.snackBar.open(
          `Player ${newActiveStatus ? 'activated' : 'deactivated'} successfully`,
          'Close',
          { duration: 3000 }
        );
        this.loadPlayers();
      },
      error: (error) => {
        console.error('Error toggling player active status:', error);
        this.snackBar.open('Error updating player status', 'Close', { duration: 3000 });
      },
    });
  }

  deletePlayer(player: PlayerModel): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      maxWidth: 'unset',
      data: {
        title: 'Delete Player',
        message: `Are you sure you want to delete player "${player.name}"? This action cannot be undone.`,
      },
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.playerService.deletePlayer(player.id).subscribe({
          next: () => {
            this.snackBar.open('Player deleted successfully', 'Close', { duration: 3000 });
            this.loadPlayers();
          },
          error: (error) => {
            console.error('Error deleting player:', error);
            this.snackBar.open('Error deleting player', 'Close', { duration: 3000 });
          },
        });
      }
    });
  }

  autoUpdate(): void {
    this.playerService.autoUpdate().subscribe({
      next: () => {
        this.snackBar.open('Player Update Job Triggered', 'Close', { duration: 3000 });
      },
      error: (error) => {
        this.snackBar.open(`Error triggering auto update: ${error}`, 'Close', { duration: 3000 });
      },
    });
  }

  getQualityLabel(quality: string): string {
    const option = this.qualityOptions.find(opt => opt.value === quality);
    return option ? option.label : quality;
  }

  getQualityClass(quality: string): string {
    switch (quality) {
      case 'bronze': return 'quality-bronze';
      case 'silver': return 'quality-silver';
      case 'gold': return 'quality-gold';
      case 'special': return 'quality-special';
      default: return '';
    }
  }
}
